import React, { useState, FormEvent, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API_URL, API_ENDPOINTS, REDIRECT_URL } from '../config/api';
import { trackEvent } from '../config/amplitude';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MessageSquare, ExternalLink } from 'lucide-react';

export interface RedditPostProps {
  id: string;
  title: string;
  selftext: string;
  author: string;
  subreddit: string;
  score: number;
  created_utc: number;
  permalink: string;
  url: string;
  keywords: string[];
  ups?: number;
  num_comments?: number;
  isCanvasView?: boolean;
  hasCommented?: boolean;
  onCommentPosted?: (postId: string, commentId: string, subreddit: string, commentText: string) => Promise<boolean> | boolean;
  position?: { x: number; y: number };
  zIndex?: number;
  isAuthenticated: boolean;
}

export const RedditPost = ({ 
  title, 
  selftext, 
  subreddit, 
  url, 
  keywords,
  ups = 0,
  num_comments = 0,
  created_utc = 0,
  id,
  hasCommented = false,
  onCommentPosted,
  isCanvasView = false,
  isAuthenticated,
  permalink
}: RedditPostProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [commentHTML, setCommentHTML] = useState('');
  const commentRef = useRef<HTMLDivElement>(null);
  const [commentsList, setCommentsList] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [selectedText, setSelectedText] = useState({ start: 0, end: 0 });
  const [showCommentBox, setShowCommentBox] = useState(false);
  const commentBoxRef = useRef<HTMLTextAreaElement>(null);

  // Check authentication on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if we have a token in localStorage
      const token = localStorage.getItem('reddit_token');
      
      // If we have a token, consider the user authenticated immediately
      if (token) {
        console.log('User is authenticated with token');
      }
      
      // Optional: Verify token with backend
      try {
        const response = await axios.get(API_ENDPOINTS.CHECK_AUTH, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        console.log('Auth check response:', response.data);
      } catch (backendError) {
        console.error('Backend auth check failed, but continuing with token-based auth:', backendError);
      }
    } catch (error) {
      console.error('Error in auth check:', error);
    }
  };

  const handleCommentChange = (e: React.FormEvent<HTMLDivElement>) => {
    const html = e.currentTarget.innerHTML;
    setCommentHTML(html);
    // Convert HTML to markdown for submission, properly capturing href
    const markdown = html.replace(/<a[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/g, '[$2]($1)');
    setComment(markdown);
  };

  const handleSubmitComment = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.POST_COMMENT,
        {
          postId: id,
          commentText: comment
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('reddit_token')}`
          }
        }
      );

      if (response.data.commentId) {
        // Only track if comment was successfully posted
        if (onCommentPosted) {
          const wasTracked = await onCommentPosted(id, response.data.commentId, subreddit, comment);
          if (!wasTracked) {
            setIsSubmitting(false);
            return;
          }
        }
        
        setCommentsList(prev => [...prev, comment]);
        setIsSubmitting(false);
        setComment("");
        setCommentHTML("");
        if (commentRef.current) {
          commentRef.current.innerHTML = "";
        }
      }
    } catch (error: any) {
      setIsSubmitting(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to post comment");
      }
    }
  };

  // Improved highlight function
  const highlightMatches = (text: string) => {
    if (!text) return <div />;
    
    let highlightedText = text;
    keywords.forEach(keyword => {
      // Create a regex that matches whole words and is case insensitive
      const regex = new RegExp(`(\\b${keyword}\\b|${keyword})`, 'gi');
      highlightedText = highlightedText.replace(
        regex,
        '<span class="bg-yellow-200 px-1 rounded">$1</span>'
      );
    });

    // Debug log to check matches
    console.log('Keywords:', keywords);
    console.log('Text:', text);
    console.log('Highlighted:', highlightedText);

    return <div dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Improved excerpt function to ensure keywords are included
  const getExcerpt = (text: string, maxLength: number = 200) => {
    if (!text) return '';
    if (isExpanded) return text;  // Return full text if expanded
    
    // Find all keyword matches
    let firstMatch = -1;
    keywords.forEach(keyword => {
      const index = text.toLowerCase().indexOf(keyword.toLowerCase());
      if (index > -1 && (firstMatch === -1 || index < firstMatch)) {
        firstMatch = index;
      }
    });

    if (firstMatch === -1) {
      // If no keyword found, return truncated start of text
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    }

    // Calculate start and end positions for excerpt
    const start = Math.max(0, firstMatch - 60);
    const end = Math.min(text.length, firstMatch + 140);
    
    // Add ellipsis if truncated
    const prefix = start > 0 ? '... ' : '';
    const suffix = end < text.length ? ' ...' : '';
    
    return prefix + text.slice(start, end) + suffix;
  };

  // Add time formatting function
  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() / 1000) - timestamp);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
      return 'just now';
    }
  };

  // Function to generate tracked link
  const generateTrackedLink = async (appLink: string) => {
    // Generate a unique tracking ID
    const trackingId = `${id}-${Date.now()}`;
    const token = localStorage.getItem('reddit_token');
    let userId = '';
    
    // Extract user ID from token if available
    if (token) {
      try {
        // Make a request to Reddit's API to get the user ID
        const headers = {
          'Authorization': `Bearer ${token}`,
          'User-Agent': 'Redipost/1.0.0'
        };
        
        // Use the token to get user info from Reddit
        const response = await axios.get('https://oauth.reddit.com/api/v1/me', { headers });
        userId = response.data.id;
        
        // Generate the URL with user ID
        const urlParams = new URLSearchParams();
        urlParams.set('url', appLink);
        urlParams.set('userId', userId);
        urlParams.set('postId', id);
        urlParams.set('subreddit', subreddit);
        return `${REDIRECT_URL}/r/${trackingId}?${urlParams.toString()}`;
      } catch (error) {
        console.error('Error getting user ID:', error);
        // Fallback to just the URL without user ID
        const urlParams = new URLSearchParams();
        urlParams.set('url', appLink);
        urlParams.set('postId', id);
        urlParams.set('subreddit', subreddit);
        return `${REDIRECT_URL}/r/${trackingId}?${urlParams.toString()}`;
      }
    } else {
      // No token, just return the URL without user ID
      const urlParams = new URLSearchParams();
      urlParams.set('url', appLink);
      urlParams.set('postId', id);
      urlParams.set('subreddit', subreddit);
      return `${REDIRECT_URL}/r/${trackingId}?${urlParams.toString()}`;
    }
  };

  // Handle link insertion
  const handleInsertLink = async () => {
    const appLink = localStorage.getItem('user_app_link');
    const appName = localStorage.getItem('user_app_name');
    
    if (!appLink || !appName) {
      alert('Please set your product name and link in Settings first');
      return;
    }

    try {
      const trackedLink = await generateTrackedLink(appLink);
      
      // Create a clickable link element
      const linkElement = document.createElement('a');
      linkElement.href = trackedLink;
      linkElement.textContent = appName;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.style.color = '#0066cc';
      linkElement.style.textDecoration = 'none';
      linkElement.onmouseover = () => linkElement.style.textDecoration = 'underline';
      linkElement.onmouseout = () => linkElement.style.textDecoration = 'none';

      // Track the event
      trackEvent('Product Link Inserted', {
        productName: appName,
        subreddit,
        postId: id,
        postTitle: title,
        trackedLink: trackedLink
      });

      // Insert the link into the contenteditable div
      if (commentRef.current) {
        const selection = window.getSelection();
        
        // Check if there's a valid selection and it's inside our contenteditable div
        if (selection && selection.rangeCount > 0 && commentRef.current.contains(selection.anchorNode)) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(linkElement);
          range.collapse(false);
        } else {
          // If no valid selection, append to the end
          commentRef.current.appendChild(linkElement);
        }

        // Update the markdown version for submission
        handleCommentChange({ currentTarget: commentRef.current } as React.FormEvent<HTMLDivElement>);
        
        // Focus back on the contenteditable div
        commentRef.current.focus();
      }
    } catch (error) {
      console.error('Error inserting link:', error);
      alert('Failed to insert link. Please try again.');
    }
  };

  return (
    <div className="mb-6 p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4 mb-2">
        {/* Upvotes box */}
        <div className="flex flex-col items-center bg-gray-50 px-3 py-2 rounded-lg min-w-[60px]">
          <span className="text-sky-500 font-semibold">
            {ups > 1000 ? `${(ups/1000).toFixed(1)}k` : ups}
          </span>
          <span className="text-xs text-gray-500">votes</span>
        </div>

        {/* Title and subreddit */}
        <div className="flex-1">
          <div className="text-lg font-semibold">
            {highlightMatches(title)}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <span>r/{subreddit}</span>
            <span>•</span>
            <span>{getTimeAgo(created_utc)}</span>
          </div>
        </div>
      </div>

      {/* Post content */}
      {selftext && (
        <div className="ml-[76px]">
          <div className="text-gray-700 mb-2">
            {highlightMatches(getExcerpt(selftext))}
          </div>
          {selftext.length > 200 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sky-500 hover:text-sky-600 text-sm flex items-center gap-1"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {/* Post Stats */}
      <div className="ml-[76px] mt-2 flex items-center gap-2">
        <a
          href={url} 
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-500 hover:underline text-sm flex items-center gap-1"
          onClick={() => trackEvent('Button Clicked', { buttonName: 'View on Reddit' })}
        >
          View on Reddit →
        </a>

        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg">
          <span className="text-sm text-gray-600">
            {num_comments > 1000 ? `${(num_comments/1000).toFixed(1)}k` : num_comments} comments
          </span>
          {hasCommented && (
            <span 
              className="inline-flex items-center justify-center bg-green-500 text-white rounded-full w-5 h-5 text-sm" 
              title="You've commented on this post"
            >
              ✓
            </span>
          )}
        </div>
      </div>

      {/* Comment box section */}
      <div className={`ml-[76px] mt-4 ${isCanvasView ? 'relative' : ''}`}>
        <form onSubmit={(e) => handleSubmitComment(e)}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={handleInsertLink}
                className={`relative group px-4 py-2 rounded-lg transition-colors ${
                  isAuthenticated 
                    ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                disabled={!isAuthenticated}
              >
                Insert Product Link
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-12 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
                  {isAuthenticated 
                    ? "Insert your product link with automatic tracking" 
                    : "Login to insert tracked product links"}
                </div>
              </button>
            </div>
            <div className={`flex items-start gap-2 ${isCanvasView ? 'bg-white' : ''}`}>
              <div
                ref={commentRef}
                contentEditable={isAuthenticated && !isSubmitting}
                onInput={handleCommentChange}
                className={`flex-1 border border-gray-300 rounded-lg p-2 min-h-[4rem] focus:outline-none focus:ring-2 focus:ring-sky-500 empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 ${isCanvasView ? 'bg-white' : ''}`}
                data-placeholder={isAuthenticated ? "Add a comment..." : "Log in to comment"}
                style={{ 
                  whiteSpace: 'pre-wrap',
                  pointerEvents: 'auto',
                  position: isCanvasView ? 'relative' : 'static',
                  zIndex: isCanvasView ? 50 : 'auto'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isCanvasView) {
                    e.currentTarget.focus();
                  }
                }}
              />
              <button
                type="submit"
                className={`px-4 py-2 text-white rounded-lg transition-colors relative overflow-hidden ${
                  isAuthenticated && !isSubmitting 
                    ? "bg-sky-500 hover:bg-sky-600" 
                    : "bg-gray-400 cursor-not-allowed"
                } ${isCanvasView ? 'z-50' : ''}`}
                disabled={!isAuthenticated || !comment.trim() || isSubmitting}
                style={{ pointerEvents: 'auto' }}
              >
                {isSubmitting ? 'Posting...' : 'Comment'}
              </button>
            </div>
            {!isAuthenticated && (
              <p className="text-sm text-sky-600 mt-1">
                Please log in with Reddit to comment
              </p>
            )}
          </div>
        </form>
      </div>

      {/* Display submitted comments */}
      <div className="ml-[76px] mt-2">
        {commentsList.map((cmt, index) => (
          <motion.div
            key={index}
            className="bg-sky-100 text-gray-900 p-2 rounded-lg mb-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {cmt}
          </motion.div>
        ))}
      </div>
    </div>
  );
};
